import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { GeraPLU } from './components/GeraPLU';
import Loja from '~/components/Loja';
import {
  InputMoney,
  InputNumber,
  InputSelect,
  InputText,
} from '~/components/NovosInputs';
import { FaWandMagicSparkles } from 'react-icons/fa6';
import { GtinContent } from './styles';
import api from '~/services/api';
import { Tooltip } from '@material-ui/core';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import {
  Control,
  FieldValues,
  FormState,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { ProdutoClonado } from '../../class/SessionStorageService';
import { TableProdutoClonado } from './components/TableProdutoClonado';
import Separator from '~/components/Separator';
import { moneyFormat } from '~/utils/functions';
import { focusAndClickElement } from '../../utils/focusAndClickElement';
import { BuscaNcm, BuscaNcmRef } from '~/components/BuscaNcm';

interface BuscaEan {
  cod_ncm: number | null;
  descricao: string;
  num_ncm: string;
}

export const FormCadastroRapido: React.FC<{
  buttonClonar: ReactNode;
  loja: any;
  lojaSelected?: number | number[];
  handleLoja: (codLoja?: number | number[]) => void;
  produtoClonado?: ProdutoClonado | null;
  onCancelTableProdutoOrigem: () => void;
  codTela: number;
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues, any>;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  formState: FormState<FieldValues>;
  clearErrors: UseFormClearErrors<FieldValues>;
}> = ({
  buttonClonar,
  loja,
  handleLoja,
  lojaSelected = undefined,
  produtoClonado = null,
  onCancelTableProdutoOrigem,
  codTela,
  register,
  control,
  getValues,
  setValue,
  formState: { errors },
  clearErrors,
}) => {
  const [resetLoja, setResetLoja] = useState(false);
  const [loadingGTIN, setLoadingGTIN] = useState(false);
  const [departamentos, setDepartamentos] = useState<any[]>([]);
  const [store, setStore] = useState<any[]>([]);
  const [codNcmPublico, setCodNcmPublico] = useState<number | null>(null);

  useEffect(() => {
    if (produtoClonado) {
      setValue('gtin', produtoClonado.p_gtin);
    }
  }, [produtoClonado, setValue]);

  const getNcms = async () => {
    const res = await api.get('/produto/ncms');
    const { data } = res.data;

    const options = data.map((ncm: any) => {
      return {
        value: ncm.cod_ncm,
        label: `${ncm.cod_ncm} - ${ncm.descricao}`,
      };
    });

    setStore(options);
  };

  useEffect(() => {
    getNcms();
  }, []);

  useEffect(() => {
    if (codNcmPublico) {
      const hasExists = store.find(
        (reg) => Number(reg.value) === Number(codNcmPublico),
      );

      if (!hasExists) return;

      setValue('cod_ncm', { label: hasExists?.label, value: hasExists?.value });
      sessionStorage.setItem(
        `ncm-${codTela}`,
        JSON.stringify({ label: hasExists?.label, value: hasExists?.value }),
      );
    }
  }, [codNcmPublico, setValue, store, codTela]);

  const getDepartamentos = useCallback(async () => {
    const res = await api.get('/produto/departamentos');
    const { data, success } = res.data;
    if (success) {
      const select = data.map((item: any) => {
        return {
          value: item.cod_departamento,
          label: `${item.cod_departamento} - ${item.descricao}`,
          cod_tipo_item: item.cod_tipo_item,
          des_tipo_item: item.des_tipo_item,
          cod_id_ctb: item.cod_id_ctb,
        };
      });

      setDepartamentos(select);
    }
  }, []);

  useEffect(() => {
    getDepartamentos();
  }, [getDepartamentos]);

  useEffect(() => {
    const input = document.querySelector(
      '[name="gtin"]',
    ) as HTMLInputElement | null;

    if (input) {
      input.focus();
      input.click();
    }
  }, []);

  useEffect(() => {
    if (produtoClonado?.p_val_venda)
      setValue('val_venda', moneyFormat(produtoClonado.p_val_venda.toFixed(2)));
  }, [produtoClonado?.p_val_venda, setValue]);

  useEffect(() => {
    if (produtoClonado?.p_qtd_estoque)
      setValue('qtd_estoque', produtoClonado?.p_qtd_estoque);
  }, [produtoClonado?.p_qtd_estoque, setValue]);

  useEffect(() => {
    if (produtoClonado?.p_des_produto) {
      setValue(
        'descricao',
        String(produtoClonado.p_des_produto).trim().slice(0, 50),
      );
      setTimeout(() => {
        focusAndClickElement('descricao');
      }, 150);
    }
  }, [produtoClonado?.p_des_produto, setValue]);

  useEffect(() => {
    setTimeout(() => {
      const hasNCM = sessionStorage.getItem(`ncm-${codTela}`);
      if (hasNCM) {
        const { value, label } = JSON.parse(hasNCM);

        setValue('cod_ncm', { label, value });
      } else {
        setValue('cod_ncm', '');
      }
    }, 150);
  }, [codTela, setValue]);

  const handleGtinPublico = async () => {
    const input = document.querySelector(
      '[name="gtin"]',
    ) as HTMLInputElement | null;

    if (!getValues('gtin')) {
      toast.warning('Necessário informar GTIN');
      setCodNcmPublico(null);
      if (input) input.focus();

      return;
    }

    clearErrors('gtin');

    try {
      setLoadingGTIN(true);

      const { data } = await api.get(
        `/produto/cadastro-rapido/busca/${getValues('gtin')}`,
      );

      if (!data.success) {
        setValue('descricao', '');
        setCodNcmPublico(null);
        return;
      }

      if (data.success) {
        const res = data.data as BuscaEan;
        setValue('descricao', res.descricao?.toUpperCase()?.trim());
        setCodNcmPublico(res.cod_ncm);
      }
    } catch (error) {
      setValue('descricao', '');
      setCodNcmPublico(null);
      if (input) input.focus();
    } finally {
      setLoadingGTIN(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', columnGap: '8px' }}>
        <GtinContent>
          <InputText
            label="GTIN"
            placeholder=""
            maxLength={14}
            name="gtin"
            autoFocus
            register={register}
            control={control}
            isError={false}
            disabled={loadingGTIN}
          />

          <div className="act-buttons">
            <Tooltip title="Buscar dados públicos">
              <button
                type="button"
                onClick={async () => {
                  await handleGtinPublico();
                }}
                aria-label="botão busca ean público"
                disabled={loadingGTIN || !!produtoClonado}
              >
                {loadingGTIN && <ClipLoader size={15} color="white" />}
                {!loadingGTIN && <FaWandMagicSparkles size={18} />}
              </button>
            </Tooltip>
            {buttonClonar}
          </div>
        </GtinContent>
        <GeraPLU setValue={setValue} getValues={getValues} codTela={codTela} />
        <div style={{ marginTop: '6.5px', width: '40%' }}>
          <Loja
            selectedLoja={lojaSelected || loja.cod_loja}
            onChange={(value) => {
              setResetLoja(false);
              handleLoja(value);
            }}
            disabled={loadingGTIN}
            isDisabled={loadingGTIN}
            resetLojas={resetLoja}
            showTooltipSingle
            requiredSignal
          />
        </div>
      </div>
      {produtoClonado && (
        <div style={{ marginTop: '-10px' }}>
          <TableProdutoClonado
            produto={produtoClonado}
            onActionClick={() => {
              setValue('gtin', '');
              onCancelTableProdutoOrigem();

              const input = document.querySelector(
                '[name="gtin"]',
              ) as HTMLInputElement | null;

              if (input) {
                input.focus();
                input.click();
              }
            }}
          />
          <div style={{ marginTop: '-15px', marginBottom: '-15px' }}>
            <Separator labelText="Destino" />
          </div>
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: produtoClonado ? '53% 23% 20%' : '50% 25% 22.5%',
          alignItems: 'center',
          columnGap: '8px',
        }}
      >
        <div style={{ position: 'relative' }}>
          {produtoClonado && (
            <small
              style={{
                position: 'absolute',
                top: '8px',
                left: '55px',
                backgroundColor: '#fff2a7',
                border: '1px solid #ffbb00',
                paddingLeft: '5px',
                paddingRight: '5px',
                color: 'black',
              }}
            >
              Produto clonado: necessário alterar descrição
            </small>
          )}
          <InputText
            label="Descrição *"
            placeholder="Informe a descrição do produto"
            maxLength={50}
            name="descricao"
            register={register}
            control={control}
            isError={!!errors.descricao}
            disabled={loadingGTIN}
            caseInput="upper"
            toLowerCase={false}
          />
        </div>
        <InputMoney
          label="Valor de Venda"
          name="val_venda"
          placeholder="0,00"
          min={0}
          max={9999999}
          maxLength={15}
          control={control}
          register={register}
          isError={!!errors.val_venda}
          style={{ backgroundColor: '#d5f3c8' }}
          disabled={loadingGTIN}
        />

        <InputNumber
          label="Estoque"
          name="qtd_estoque"
          step="1"
          min={1}
          max={9999}
          maxLength={4}
          placeholder="1"
          register={register}
          control={control}
          isError={!!errors.qtd_estoque}
          style={{ backgroundColor: '#bcdaf7' }}
          disabled={loadingGTIN}
          disableWheel
        />
      </div>

      {!produtoClonado && (
        <>
          <div>
            <InputSelect
              label="Departamento *"
              placeholder="Selecione o departamento"
              name="cod_departamento"
              register={register}
              isError={!!errors.cod_departamento}
              control={control}
              options={departamentos}
              changeSelected={(selected: any) => {
                setValue('cod_departamento', selected);

                clearErrors('cod_departamento');
              }}
            />
          </div>
          <div>
            <BuscaNcm
              name="cod_ncm"
              control={control}
              isUpdate={false}
              init
              invalid={!!errors?.cod_ncm}
              isDisabled={false}
              placeholder="Selecione o NCM"
            />
          </div>
        </>
      )}
    </div>
  );
};
